import { MessageDescriptor } from 'react-intl';
import {
  AppPermissionLevel,
  PermissionGroup,
  PoliciesPermissionSetItems,
} from 'common/constants/permissions';
import { TPermissionContext } from 'common/contexts/PermissionContext';
import { IPermissionLevelContext } from 'common/contexts/PermissionLevelContext';
import { IPermissionsArray } from 'modules/permissions/interfaces/permissions';
import { PermissionGroupLevel } from 'types/permission.types';
import {
  PermissionGroupsClubLevel,
  PermissionGroupsCorporateLevel,
  PermissionGroupsCorporateOrClubLevel,
  PermissionGroupsPeakLevel,
  PermissionsArrayClubLevel,
  PermissionsArrayCorporateLevel,
  PermissionsArrayCorporateOrClubLevel,
  PermissionsArrayPeakLevel,
} from 'modules/permissions/constants';
import commonMessages from 'common/messages/messages';

export function getInitialPermissionContext(): TPermissionContext {
  const permissionContext: Partial<TPermissionContext> = {};

  for (const pKey of Object.keys(PoliciesPermissionSetItems)) {
    permissionContext[pKey] = false;
  }

  return permissionContext as TPermissionContext;
}

export function getInitialPermissionLevelContext(): IPermissionLevelContext {
  return {
    LEVEL_ROLE_VIEW: false,
    LEVEL_ROLE_CREATE: false,
    LEVEL_ROLE_EDIT: false,
    LEVEL_ROLE_CHANGE_STATUS: false,
    LEVEL_POLICY_VIEW: false,
    LEVEL_POLICY_CREATE: false,
    LEVEL_POLICY_EDIT: false,
    LEVEL_POLICY_CHANGE_STATUS: false,
    LEVEL_HELP_INFORMATION_VIEW: false,
    LEVEL_HELP_INFORMATION_CREATE: false,
    LEVEL_HELP_INFORMATION_EDIT: false,
    LEVEL_HELP_INFORMATION_CHANGE_STATUS: false,
    LEVEL_SIGN_IN_AS_SUPPORT_USER: false,
    LEVEL_TRANSFER_OWNERSHIP: false,
    LEVEL_GENERAL_INFO_VIEW: false,
    LEVEL_GENERAL_INFO_EDIT: false,
    LEVEL_BANK_INFO_VIEW: false,
    LEVEL_BANK_INFO_EDIT: false,
    LEVEL_BILLING_INFO_VIEW: false,
    LEVEL_BILLING_INFO_EDIT: false,
    LEVEL_FEE_RATES_VIEW: false,
    LEVEL_FEE_RATES_EDIT: false,
    LEVEL_PROCESSOR_CONFIG_VIEW: false,
    LEVEL_PROCESSOR_CONFIG_EDIT: false,
    LEVEL_TAXES_INFO_VIEW: false,
    LEVEL_TAXES_INFO_EDIT: false,
    LEVEL_OTHER_INFO_VIEW: false,
    LEVEL_OTHER_INFO_EDIT: false,
    LEVEL_REQUIRED_FIELDS_VIEW: false,
    LEVEL_REQUIRED_FIELDS_EDIT: false,
    LEVEL_PAY_PERIOD_SETTINGS_VIEW: false,
    LEVEL_PAY_PERIOD_SETTINGS_CREATE: false,
    LEVEL_PAY_PERIOD_SETTINGS_EDIT: false,
    LEVEL_PAY_PERIOD_VIEW: false,
    LEVEL_PAY_PERIOD_EDIT: false,
    LEVEL_PAY_PERIOD_CHANGE_STATUS: false,
    LEVEL_INTEGRATION_VIEW: false,
    LEVEL_INTEGRATION_EDIT: false,
    LEVEL_INTEGRATION_CHANGE_STATUS: false,
    LEVEL_EMPLOYEE_EDIT: false,
    LEVEL_EMPLOYEE_CHANGE_STATUS: false,
  };
}

export function getAppPermissionLevelGroups(level: AppPermissionLevel): string[] {
  switch (level) {
    case AppPermissionLevel.Club: {
      return Object.values(PermissionGroupsClubLevel);
    }
    case AppPermissionLevel.Corporate: {
      return Object.values(PermissionGroupsCorporateLevel);
    }
    case AppPermissionLevel.ClubCorporate: {
      return Object.values(PermissionGroupsCorporateOrClubLevel);
    }
    case AppPermissionLevel.Peak: {
      return Object.values(PermissionGroupsPeakLevel);
    }
    default: {
      return [];
    }
  }
}

export function getAppPermissionLevelByGroup(group: PermissionGroup): AppPermissionLevel | null {
  if (Object.values<string>(PermissionGroupsCorporateOrClubLevel).includes(group)) {
    return AppPermissionLevel.ClubCorporate;
  } else if (Object.values<string>(PermissionGroupsCorporateLevel).includes(group)) {
    return AppPermissionLevel.Corporate;
  } else if (Object.values<string>(PermissionGroupsClubLevel).includes(group)) {
    return AppPermissionLevel.Club;
  } else if (Object.values<string>(PermissionGroupsPeakLevel).includes(group)) {
    return AppPermissionLevel.Peak;
  }

  return null;
}

export function getAppPermissionLevelGroupOptions(
  level: AppPermissionLevel,
): IPermissionsArray<PermissionGroupLevel> | null {
  switch (level) {
    case AppPermissionLevel.Club: {
      return PermissionsArrayClubLevel;
    }
    case AppPermissionLevel.Corporate: {
      return PermissionsArrayCorporateLevel;
    }
    case AppPermissionLevel.ClubCorporate: {
      return PermissionsArrayCorporateOrClubLevel;
    }
    case AppPermissionLevel.Peak: {
      return PermissionsArrayPeakLevel;
    }
    default: {
      return null;
    }
  }
}

export function getAppPermissionLevelLabel(level: AppPermissionLevel): MessageDescriptor | null {
  switch (level) {
    case AppPermissionLevel.Club: {
      return commonMessages.clubTitle;
    }
    case AppPermissionLevel.Corporate: {
      return commonMessages.corporateTitle;
    }
    case AppPermissionLevel.ClubCorporate: {
      return commonMessages.clubAndCorporateTitle;
    }
    case AppPermissionLevel.Peak: {
      return commonMessages.peakTitle;
    }
    default: {
      return null;
    }
  }
}
